/* eslint-disable @typescript-eslint/no-explicit-any */

export interface Quad {
  /** Corners in TL → TR → BR → BL order, in canvas pixel coordinates. */
  corners: [[number, number], [number, number], [number, number], [number, number]];
  /** Contour area in canvas pixels². */
  area: number;
}

export interface ContourResult {
  quad: Quad | null;
}

/**
 * Runs the document contour detection pipeline on a single ImageData frame.
 *
 * Pipeline: RGBA → Grayscale → GaussianBlur → Canny → Dilate →
 *           findContours → approxPolyDP → largest convex quadrilateral.
 *
 * All OpenCV Mats created here are deleted before returning.
 */
export function detectDocumentContour(
  cv: any,
  imageData: ImageData,
): ContourResult {
  const { width: w, height: h } = imageData;
  const imageArea = w * h;

  const src = cv.matFromImageData(imageData);
  const gray = new cv.Mat();
  const blurred = new cv.Mat();
  const edges = new cv.Mat();
  const dilated = new cv.Mat();
  const kernel = cv.Mat.ones(3, 3, cv.CV_8U);
  const contours = new cv.MatVector();
  const hierarchy = new cv.Mat();

  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);
    cv.Canny(blurred, edges, 50, 150);
    // Dilate to bridge small gaps in the document border.
    cv.dilate(edges, dilated, kernel, new cv.Point(-1, -1), 2);

    cv.findContours(
      dilated,
      contours,
      hierarchy,
      cv.RETR_EXTERNAL,
      cv.CHAIN_APPROX_SIMPLE,
    );

    let bestQuad: Quad | null = null;
    let maxArea = 0;

    for (let i = 0; i < contours.size(); i++) {
      const contour = contours.get(i); // owned by MatVector — do not delete
      const area = cv.contourArea(contour);

      // Skip contours too small to be a document (< 5% of image area).
      if (area < imageArea * 0.05) continue;

      const approx = new cv.Mat();
      try {
        const perimeter = cv.arcLength(contour, true);
        cv.approxPolyDP(contour, approx, 0.02 * perimeter, true);

        if (
          approx.rows === 4 &&
          cv.isContourConvex(approx) &&
          area > maxArea
        ) {
          maxArea = area;
          const d = approx.data32S;
          const raw: [number, number][] = [
            [d[0], d[1]],
            [d[2], d[3]],
            [d[4], d[5]],
            [d[6], d[7]],
          ];
          bestQuad = {
            corners: sortQuadCorners(raw),
            area,
          };
        }
      } finally {
        approx.delete();
      }
    }

    return { quad: bestQuad };
  } finally {
    src.delete();
    gray.delete();
    blurred.delete();
    edges.delete();
    dilated.delete();
    kernel.delete();
    contours.delete();
    hierarchy.delete();
  }
}

/** Orders four corner points as TL → TR → BR → BL. */
function sortQuadCorners(
  pts: [number, number][],
): [[number, number], [number, number], [number, number], [number, number]] {
  const byY = [...pts].sort((a, b) => a[1] - b[1]);
  const top = byY.slice(0, 2).sort((a, b) => a[0] - b[0]);
  const bot = byY.slice(2).sort((a, b) => a[0] - b[0]);
  return [top[0], top[1], bot[1], bot[0]] as [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
  ];
}
